<template>
  <div class="vue-fortuneWheel">
    <div
      ref="fortuneWheelBox"
      class="fortuneWheel-box"
      :style="fortuneWheelStyle"
      @transitionend="onRotateEnd"
      @webkitTransitionend="onRotateEnd"
    >
      <canvas
        v-if="type === 'canvas'"
        ref="fortuneWheelCanvas"
        :width="radius * 2"
        :height="radius * 2"
      />
      <slot name="wheel" v-else />
    </div>
    <div class="fortuneWheel-wrapper">
      <div class="btn-container" @click="onRotateStart">
        <div
          v-if="type === 'canvas'"
          class="fortuneWheel-btn"
          :style="{ width: btnWidth, height: btnWidth}"
        >{{ btnText }}</div>
        <slot v-else name="button" />        
        <div style="width: 100%; height: 100%; position: absolute; left: 0px; top: 0px; color: #ffffff; font-size: 18px; margin-top: 38px; text-shadow: 2px 2px #000000; font-weight: 900;  z-index: 500;">{{$t('START')}}</div>
      </div>      
    </div>
  </div>
</template>

<script>
import { randomNum, sumBy } from "../utils/number";
import axios from "axios";
import config from "../config.js";

export default {
  name: "FortuneWheel",
  props: {
    type: {
      type: String,
      default: "canvas"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    radius: {
      type: Number,
      default: 250
    },
    textRadius: {
      type: Number,
      default: 190
    },
    textLength: {
      type: Number,
      default: 6
    },
    lineHeight: {
      type: Number,
      default: 20
    },
    borderWidth: {
      type: Number,
      default: 0
    },
    borderColor: {
      type: String,
      default: "transparent"
    },
    btnText: {
      type: String,
      default: "GO"
    },
    btnWidth: {
      type: String,
      default: "170px"
    },
    fontSize: {
      type: Number,
      default: 34
    },
    duration: {
      type: Number,
      default: 6000
    },
    timingFun: {
      type: String,
      default: "cubic-bezier(0.36, 0.95, 0.64, 1)"
    },
    angleBase: {
      type: Number,
      default: 10
    },
    prizeId: {
      type: Number,
      default: 0
    },
    prizes: {
      type: Array,
      default: () => []
    },
    publicKey: {
        type: String,
        default: ""
    }
  },
  data() {
    return {
      isRotating: false,
      rotateEndDeg: 0,
      prizeRes: {}
    };
  },
  computed: {
    probabilityTotal() {
      return sumBy(this.prizes, row => row.probability);
    },
    prizesIdArr() {
      const idArr = [];
      this.prizes.forEach(row => {
        const count = Math.round(row.probability * this.decimalSpaces);
        const arr = new Array(count).fill(row.id);
        idArr.push(...arr);
      });
      return idArr;
    },
    decimalSpaces() {
      const sortArr = [...this.prizes].sort((a, b) => {
        const aRes = String(a.probability).split(".")[1];
        const bRes = String(b.probability).split(".")[1];
        const aLen = aRes ? aRes.length : 0;
        const bLen = bRes ? bRes.length : 0;
        return bLen - aLen;
      });
      const maxRes = String(sortArr[0].probability).split(".")[1];
      const idx = maxRes ? maxRes.length : 0;
      return [1, 10, 100, 1000][idx > 4 ? 4 : idx];
    },
    rotateDuration() {
      return this.isRotating ? this.duration / 1000 : 0;
    },
    fortuneWheelStyle() {
      return {
        "-webkit-transform": `rotateZ(${this.rotateEndDeg}deg)`,
        transform: `rotateZ(${this.rotateEndDeg}deg)`,
        "-webkit-transition-duration": `${this.rotateDuration}s`,
        "transition-duration": `${this.rotateDuration}s`,
        "-webkit-transition-timing-function:": this.timingFun,
        "transition-timing-function": this.timingFun
      };
    },
    rotateBase() {
      let angle = this.angleBase * 360;
      if (this.angleBase < 0) angle -= 360;
      return angle;
    },
    canRotate() {
      return (
        !this.disabled && !this.isRotating
      );
    }
  },
  watch: {
    prizeId(newVal) {
      if (!this.isRotating) return;
      let newAngle = this.getTargetDeg(newVal);
      if (this.angleBase < 0) newAngle -= 360;

      const prevEndDeg = this.rotateEndDeg;
      let nowEndDeg = this.angleBase * 360 + newAngle;
      const angle = 360 * Math.floor((nowEndDeg - prevEndDeg) / 360);
      if (this.angleBase >= 0) {
        nowEndDeg += Math.abs(angle);
      } else {
        nowEndDeg += -360 - angle;
      }
      this.rotateEndDeg = nowEndDeg;
    }
  },
  created() {
    this.checkProbability();
  },
  mounted() {
    if (this.type === "canvas") this.drawCanvas();
  },
  methods: {
    checkProbability() {
      return true;
    },
    drawCanvas() {
      const canvas = this.$refs.fortuneWheelCanvas;
      if (canvas.getContext) {
        const arc = Math.PI / (this.prizes.length / 2);
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, this.radius * 2, this.radius * 2);
        ctx.strokeStyle = this.borderColor;
        ctx.lineWidth = this.borderWidth * 2;
        ctx.font = `${this.fontSize}px Noto Sans KR`;
        this.prizes.forEach((row, i) => {
          const angle = i * arc - Math.PI / 2;
          ctx.fillStyle = row.bgColor;
          ctx.beginPath();
          ctx.arc(
            this.radius,
            this.radius,
            this.radius - this.borderWidth,
            angle,
            angle + arc,
            false
          );
          ctx.stroke();
          ctx.arc(this.radius, this.radius, 0, angle + arc, angle, true);
          ctx.fill();
          ctx.save();
          ctx.fillStyle = row.color;
          ctx.translate(
            this.radius + Math.cos(angle + arc / 2) * this.textRadius,
            this.radius + Math.sin(angle + arc / 2) * this.textRadius
          );
          ctx.rotate(angle + arc / 2 + Math.PI / 2);
          if (row.name.length > this.textLength) {
            const content = [
              row.name.substring(0, this.textLength),
              row.name.substring(this.textLength)
            ];
            for (let j = 0; j < content.length; j++) {
              ctx.fillText(
                content[j],
                -ctx.measureText(content[j]).width / 2,
                (j + 1) * this.lineHeight
              );
            }
          } else {
            ctx.fillText(
              row.name,
              -ctx.measureText(row.name).width / 2,
              this.lineHeight
            );
          }
          ctx.restore();
        });
      }
    },
    onRotateStart: async function() {
      if (!this.canRotate) {
          if (this.disabled) {
           this.$emit("onRotateStartError", 'event_ended');
          }          
          return;
      }

      let ret = await axios.post(`${config.api.stat_url}/preregist/event/spin2`, {publicKey: this.publicKey, language: this.$i18n.locale});
      if (ret.data.result == false) {
        this.$emit("onRotateStartError", ret.data.err);
        return;
      }
        this.isRotating = true;
        const prizeId = ret.data.selectedId;
        this.rotateEndDeg = this.rotateBase + this.getTargetDeg(prizeId);
        console.log('rotateEndDeg', this.getTargetDeg(prizeId));
      this.$emit("onRotateStart", ret.data);
    //   this.isRotating = true;
    //   const prizeId = this.prizeId || this.getRandomPrize();
    //   this.rotateEndDeg = this.rotateBase + this.getTargetDeg(prizeId);
    //   this.$emit("onRotateStart");

    },
    onRotateEnd() {
      this.isRotating = false;
      this.rotateEndDeg %= 360;
      this.$emit("onRotateEnd", this.prizeRes);
    },
    getRandomPrize() {
      const prizeId = this.prizesIdArr[
        randomNum(0, 100 * this.decimalSpaces - 1)
      ];
      return prizeId;
    },
    getTargetDeg(prizeId) {
      const angle = 360 / this.prizes.length;
      const num = this.prizes.findIndex(row => row.id === prizeId);
      this.prizeRes = this.prizes.filter(row => row.id === prizeId)[0] || [];
      return 360 - (angle * num);
    }
  }
};
</script>

<style lang="scss" scoped>
.vue-fortuneWheel {
  display: inline-block;
  position: relative;
  font-size: 0;
  overflow: hidden;
  user-select: none;
  max-width: 100%;
  &,
  * {
    box-sizing: border-box;
  }
}

.fortuneWheel-box {
  transition-property: transform;
  canvas,
  img {
    display: block;
    width: 100%;
  }
}

.fortuneWheel-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-container {
  position: absolute;
  cursor: pointer;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 104px;
  height: 104px;
}

.fortuneWheel-btn {
  position: relative;
  width: 100%;
  height: 100%;
  background: #fff;
  border: 6px solid #fff;
  border-radius: 50%;
  background: #15bd96;
  color: #fff;
  text-align: center;
  font-size: 42px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  &:after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    border-left: 18px solid transparent;
    border-right: 18px solid transparent;
    border-bottom: 22px #fff solid;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
  }
  &:before {
    content: "";
    display: block;
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-bottom: 18px #15bd96 solid;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translate(-50%) translateY(6px);
    z-index: 10;
  }
}
</style>